import AnnouncementBar from '../home/components/AnnouncementBar';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';

const keyPoints = [
  { icon: 'ri-install-line', title: 'Kolay Kurulum ve Yönetim', desc: 'Mevcut VMware ortamınıza sorunsuz entegrasyon, karmaşık yapılandırma gerekmez.' },
  { icon: 'ri-hard-drive-2-line', title: 'Mevcut Donanımda Çalışır', desc: 'DR için kullanılan donanımın üzerinde çalışır; ek donanım yatırımı gerekmez.' },
  { icon: 'ri-percent-line', title: 'DR Maliyetlerini %60 Azaltır', desc: 'Kuruluşların çoğu VMware DR maliyetlerini %60 veya daha fazla azaltır.' },
];

const resiliencyFeatures = [
  'Değişmez anlık görüntüler verinin değiştirilmesini engeller',
  'Global Satır İçi Tekilleştirme aktarımları hızlandırır ve maliyetleri düşürür',
  'İki faktörlü kimlik doğrulama ve sertleştirilmiş ortam',
];

const testingPoints = [
  'DR testleri için izole sanal lab',
  'Testler üretim veri korumasını bozmaz',
  'Kesintiden çevrimiçiye dakikalar içinde',
];

const expandPoints = [
  { icon: 'ri-server-line', label: 'On-Premises Felaket Koruması' },
  { icon: 'ri-test-tube-line', label: 'Test-Dev için Kullanım' },
];

const ioprotectJsonLd = { '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'ioProtect', applicationCategory: 'BusinessApplication', operatingSystem: 'Any', description: 'ioProtect ile kurumsal veri koruma ve yedekleme. Anlık görüntü tabanlı yedekleme, uzun süreli saklama ve hızlı kurtarma.', url: `${SITE_URL}/ioprotect` };

export default function IoProtectPage() {
  return (
    <div className="min-h-screen w-full bg-white">
      <SEOHead title="ioProtect | Kurumsal Veri Koruma ve Yedekleme | VergeOS" description="ioProtect ile kurumsal sınıf veri koruma. Anlık görüntü tabanlı yedekleme, uzun süreli veri saklama ve hızlı nokta-içi kurtarma." keywords="ioProtect, veri koruma, yedekleme, backup, VergeOS, veri kurtarma" canonicalPath="/ioprotect" jsonLd={ioprotectJsonLd} />
      <AnnouncementBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full overflow-hidden bg-[#0f1117] py-24 text-center">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'repeating-linear-gradient(45deg,#e8600a 0,#e8600a 1px,transparent 0,transparent 50%)', backgroundSize: '20px 20px' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1117]/60 to-[#0f1117]/90" />
          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <div className="inline-block bg-[#e8600a]/20 text-[#e8600a] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Özellikler</div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              VergeIO <span className="text-[#e8600a]">ioProtect</span>
            </h1>
            <p className="text-xl text-gray-300 mb-3 font-medium">DR Basitleştirildi</p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {['Kolay Kurulum ve Yönetim', 'Mevcut Donanımda Çalışır', 'DR Maliyetlerini %60 Azaltır'].map(item => (
                <span key={item} className="bg-white/10 text-white text-xs px-3 py-1 rounded-full">{item}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Sorunsuz Entegre DR</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">VergeIO'nun ioProtect, VergeOS'a entegre olup Sanal Makinelerinin uzak DR sitesine neredeyse gerçek zamanlı replikasyonunu sağlar. Ortamınıza sorunsuz bağlanır ve DR için kullanılan mevcut donanım üzerinde çalışır.</p>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {keyPoints.map(p => (
                    <div key={p.title} className="bg-[#f8fafc] rounded-lg p-4 text-center border border-gray-100">
                      <span className="w-8 h-8 flex items-center justify-center bg-[#e8600a]/10 rounded-lg text-[#e8600a] mx-auto mb-2"><i className={`${p.icon} text-base`}></i></span>
                      <p className="text-xs font-semibold text-[#1e293b]">{p.title}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/9f3f8df8a1142e3ddb13f66f8d9ffeb5.jpg" alt="ioProtect Mimarisi" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Resiliency */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=data%20resiliency%20and%20recovery%20diagram%20showing%20VergeOS%20deduplication%20with%20optimized%20remote%20sync%20and%20immutable%20snapshots%20providing%20WAN%20optimized%20replication%20and%20ransomware%20protection%2C%20technical%20infrastructure%20dark%20background%20orange%20circuit%20visualization%20professional%20enterprise&width=580&height=380&seq=ioprotect_resil1&orientation=landscape" alt="ioProtect Dayanıklılık" className="w-full h-full object-top" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Dayanıklılık ve Kurtarmayı İyileştirin</h2>
                <ul className="space-y-4 mb-6">
                  {resiliencyFeatures.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#e8600a] text-base shrink-0 mt-0.5"></i>{item}
                    </li>
                  ))}
                </ul>
                <p className="text-gray-600 text-sm leading-relaxed">VergeOS ortamı sertleştirilmiştir ve erişim için iki faktörlü kimlik doğrulama gerektirir. ioProtect, güvenli felaket kurtarma ve fidye yazılımı dayanıklılığı sağlar. VergeOS müşterileri depolama özellikleri ve tasarrufları SAN değişimi olarak VergeOS kullanmayı o kadar cazip bulmaktadır.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testing */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Testi Basitleştirin</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">ioProtect, DR testini kolaylaştırmak için VergeOS'u kullanır. VergeOS'un Sanal Veri Merkezi Teknolojisini (VDC) kullanan BT, üretimi veya veri koruma sürecini kesintiye uğratmayacak izole bir sanal labda belirli iş yüklerini veya tüm veri merkezini başlatabilir.</p>
                <ul className="space-y-3">
                  {testingPoints.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#e8600a] text-base shrink-0 mt-0.5"></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=ioProtect%20testing%20architecture%20showing%20isolated%20virtual%20lab%20VDC%20for%20DR%20testing%20that%20does%20not%20impact%20production%20VMware%20environment%2C%20on-premise%20and%20off-site%20DR%20site%20diagram%20with%20VergeOS%2C%20professional%20technical%20visualization%20dark%20background%20orange%20highlights%20enterprise%20disaster%20recovery&width=580&height=400&seq=ioprotect_test1&orientation=landscape" alt="ioProtect DR Testi" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Expand Beyond DR */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-4">DR'ın Ötesine Genişleyin</h2>
            <p className="text-gray-600 mb-10 text-sm leading-relaxed max-w-2xl mx-auto">DR kullanım senaryosunun tamamen ötesine geçin ve ioProtect'i Test-Dev, Kalite Güvencesi ve raporlama için kullanın. Bazı müşteriler VergeOS'u on-premises DR koruması için atlama taşı olarak kullanarak nihayetinde tam bir VMware değişimine doğru ilerler.</p>
            <div className="flex flex-wrap justify-center gap-6">
              {expandPoints.map(p => (
                <div key={p.label} className="flex items-center gap-3 bg-white rounded-lg px-6 py-4 border border-gray-100">
                  <span className="w-8 h-8 flex items-center justify-center text-[#e8600a]"><i className={`${p.icon} text-xl`}></i></span>
                  <span className="font-medium text-[#1e293b] text-sm">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0f1117]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">ioProtect ile DR Maliyetlerinizi Azaltın</h2>
            <p className="text-gray-400 mb-8 text-sm">ioProtect'in felaket kurtarmanızı nasıl basitleştireceğini ve maliyetleri nasıl düşüreceğini öğrenin.</p>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
